import { useEffect } from "react";

// Builds a single floating paywall from the INLINE regwall markup Zephr injects.
//
// The current Zephr config no longer wraps the paywall in a <dialog id="zephr-zone-popup">
// (window.Zephr.openModal is gone); it injects, once per gated marker (~10x), a
// `#paywall-prompt` + a sibling `<section id="zephr-registration-form">` straight into the
// gated content. Left alone, all ~10 copies render inline inside the article. This hook keeps
// ONE copy, lifts it into a body-level fixed-position card over a dimmed backdrop, hides the
// rest, and drives the two-step prompt→form reveal. Brand colour/name come from the per-brand
// `--brand-*` custom properties (globals.scss); the card/backdrop styling lives in paywall.scss.
const PROMPT = "#paywall-prompt";
const FORM = "#zephr-registration-form";
const CARD_ID = "paywall-card";
const BACKDROP_ID = "paywall-backdrop";

export const usePaywallFlow = () => {
  useEffect(() => {
    // Blaize's injected form script wires a submit handler by id (getElementById returns the
    // FIRST match), so with duplicate ids every injected copy arms the same form's submit — one
    // click can fire many identical /blaize/token-exchange POSTs (one email each). Collapse
    // concurrent duplicates to a single in-flight request. Installed lazily on first regwall.
    const nativeOpen = XMLHttpRequest.prototype.open;
    const nativeSend = XMLHttpRequest.prototype.send;
    let xhrPatched = false;
    const installXhrDedup = () => {
      if (xhrPatched) {
        return;
      }
      xhrPatched = true;
      const inFlight = new Set<string>();
      XMLHttpRequest.prototype.open = function (
        this: XMLHttpRequest & { __pwKey?: string },
        method: string,
        url: string | URL,
        ...rest: unknown[]
      ) {
        this.__pwKey =
          method.toUpperCase() === "POST"
            ? new URL(url.toString(), window.location.href).pathname
            : undefined;
        return (nativeOpen as (...args: unknown[]) => void).call(this, method, url, ...rest);
      } as typeof XMLHttpRequest.prototype.open;
      XMLHttpRequest.prototype.send = function (
        this: XMLHttpRequest & { __pwKey?: string },
        body?: Document | XMLHttpRequestBodyInit | null
      ) {
        const key = this.__pwKey;
        if (key && key.includes("token-exchange")) {
          if (inFlight.has(key)) {
            return;
          }
          inFlight.add(key);
          this.addEventListener("loadend", () => inFlight.delete(key), { once: true });
        }
        return nativeSend.call(this, body);
      } as typeof XMLHttpRequest.prototype.send;
    };

    // Consent-first: while Cookiebot's banner is shown the paywall is inert (visible but
    // non-interactive); once the user answers, it becomes active. Returning visitors
    // (already responded) are never gated. Cookiebot renders a normal high-z-index <div>,
    // so the card/backdrop sit below it (paywall.scss z-index budget) and it paints on top.
    let consentBlocked =
      (window as unknown as { Cookiebot?: { hasResponse?: boolean } }).Cookiebot
        ?.hasResponse === false;
    const applyConsent = () => {
      const card = document.getElementById(CARD_ID);
      if (!card) {
        return;
      }
      if (consentBlocked) {
        card.setAttribute("inert", "");
      } else {
        card.removeAttribute("inert");
      }
    };
    const onConsentBlock = () => {
      consentBlocked = true;
      applyConsent();
    };
    const onConsentClear = () => {
      consentBlocked = false;
      applyConsent();
    };
    const CONSENT_CLEAR_EVENTS = [
      "CookiebotOnAccept",
      "CookiebotOnDecline",
      "CookiebotOnConsentReady",
    ];
    window.addEventListener("CookiebotOnDialogDisplay", onConsentBlock);
    CONSENT_CLEAR_EVENTS.forEach((name) => window.addEventListener(name, onConsentClear));

    // Rewire the prompt buttons. The injected template is dialog-era: its inline onclick
    // handlers call document.getElementById('zephr-zone-popup').close() — which no longer
    // exists and throws — and the SIGN IN/REGISTER anchors jump to dead #hash targets.
    // Override them with the in-place two-step reveal (prompt → chosen form view).
    const wirePrompt = (prompt: HTMLElement, form: HTMLElement | null) => {
      const reveal = (view: "register" | "login") => (event: Event) => {
        event.preventDefault();
        prompt.style.display = "none";
        if (!form) {
          return;
        }
        form.style.display = "block";
        const register = form.querySelector<HTMLElement>(".blaize-form-register");
        const login = form.querySelector<HTMLElement>(".blaize-form-login");
        if (register) {
          register.style.display = view === "register" ? "block" : "none";
        }
        if (login) {
          login.style.display = view === "login" ? "block" : "none";
        }
      };
      const registerBtn = prompt.querySelector<HTMLElement>(".reg-btn");
      const signinBtn = prompt.querySelector<HTMLElement>(".signin-btn");
      if (registerBtn) {
        registerBtn.onclick = reveal("register");
      }
      if (signinBtn) {
        signinBtn.onclick = reveal("login");
      }
    };

    // Wire the in-form view switch ("Already registered? → login" / "Don't have an
    // account? → register"). Blaize's own injected script does this too, but it runs on
    // DOMContentLoaded and may miss late client-side injection — own it so the toggle
    // always works. Submit is left entirely to Blaize (it posts to Zephr).
    const wireFormToggle = (form: HTMLElement) => {
      const register = form.querySelector<HTMLElement>(".blaize-form-register");
      const login = form.querySelector<HTMLElement>(".blaize-form-login");
      if (!register || !login) {
        return;
      }
      const show = (view: "register" | "login") => (event: Event) => {
        event.preventDefault();
        register.style.display = view === "register" ? "block" : "none";
        login.style.display = view === "login" ? "block" : "none";
      };
      const toLogin = form.querySelector<HTMLElement>(".blaize-to-login a");
      const toRegister = form.querySelector<HTMLElement>(".blaize-to-register a");
      if (toLogin) {
        toLogin.onclick = show("login");
      }
      if (toRegister) {
        toRegister.onclick = show("register");
      }
    };

    // The form's associated section is the #zephr-registration-form within the same gated
    // block; fall back to the first one in the document.
    const formFor = (prompt: HTMLElement): HTMLElement | null => {
      const scope = prompt.closest(".zephr-gated") ?? prompt.parentElement ?? document;
      return (
        scope.querySelector<HTMLElement>(FORM) ?? document.querySelector<HTMLElement>(FORM)
      );
    };

    // Remove every injected regwall EXCEPT the one now living in our card.
    const removeStrayRegwalls = () => {
      document.querySelectorAll<HTMLElement>(PROMPT).forEach((prompt) => {
        if (prompt.closest(`#${CARD_ID}`)) {
          return; // our keeper
        }
        (prompt.closest(".zephr-gated") ?? prompt).remove();
      });
      // Orphan forms whose prompt was removed above.
      document.querySelectorAll<HTMLElement>(FORM).forEach((f) => {
        if (!f.closest(`#${CARD_ID}`) && !f.closest(".zephr-gated")?.querySelector(PROMPT)) {
          (f.closest(".zephr-gated") ?? f).remove();
        }
      });
    };

    const build = () => {
      // Already built: just keep clearing any late-injected duplicates and re-apply consent.
      if (document.getElementById(CARD_ID)) {
        removeStrayRegwalls();
        applyConsent();
        return;
      }
      const keeper = document.querySelector<HTMLElement>(PROMPT);
      if (!keeper) {
        return; // nothing injected yet
      }
      installXhrDedup();

      const form = formFor(keeper);

      const backdrop = document.createElement("div");
      backdrop.id = BACKDROP_ID;
      const card = document.createElement("div");
      card.id = CARD_ID;

      // Move the keeper prompt (+ its form) into the fixed card. Moving to <body> keeps the
      // fixed positioning free of any transformed article ancestor.
      card.appendChild(keeper);
      if (form) {
        card.appendChild(form);
      }
      document.body.appendChild(backdrop);
      document.body.appendChild(card);
      // Signal the gated background to show its blurred fake placeholders and lock scroll
      // (paywall.scss). Set on both <html> and <body> since the window is the scroller here.
      document.documentElement.classList.add("paywall-active");
      document.body.classList.add("paywall-active");

      // Two-step: prompt visible, form hidden until the user picks sign-in or register.
      keeper.style.display = "block";
      if (form) {
        form.style.display = "none";
      }

      wirePrompt(keeper, form);
      if (form) {
        wireFormToggle(form);
      }
      removeStrayRegwalls();
      applyConsent();
    };

    build();

    // Zephr injects the regwall client-side, possibly after mount and in several passes.
    const observer = new MutationObserver(() => build());
    observer.observe(document.body, { childList: true, subtree: true });

    const poll = window.setInterval(build, 300);
    const pollTimeout = window.setTimeout(() => window.clearInterval(poll), 10000);

    return () => {
      if (xhrPatched) {
        XMLHttpRequest.prototype.open = nativeOpen;
        XMLHttpRequest.prototype.send = nativeSend;
      }
      window.removeEventListener("CookiebotOnDialogDisplay", onConsentBlock);
      CONSENT_CLEAR_EVENTS.forEach((name) =>
        window.removeEventListener(name, onConsentClear)
      );
      observer.disconnect();
      window.clearInterval(poll);
      window.clearTimeout(pollTimeout);
      document.getElementById(BACKDROP_ID)?.remove();
      document.getElementById(CARD_ID)?.remove();
      document.documentElement.classList.remove("paywall-active");
      document.body.classList.remove("paywall-active");
    };
  }, []);
};
